import { createContext, useContext, ReactNode, useState } from "react"

// The mobile-web map/list toggle lives in the top header bar, but the sheet ref
// + revealed state + toggleMap that drive it live deep in the page content
// (VerticalLayoutContent / GuideShell), which is a sibling of the header (both
// are rendered by the route layout via SiteHeader). Props can't flow up across
// that boundary, so the page publishes its toggle config here and the header
// reads it. The provider is hosted at the root, above every SiteHeader + Outlet,
// so it covers the authenticated shell AND the logged-out public guide-read
// layout (both render their own SiteHeader). `config` is null whenever no page
// is in the mobile map-behind-sheet branch — the header then renders nothing.
export interface MapToggleConfig {
  // True while the map is revealed (sheet below its docked snap) — drives the
  // header button's icon + label.
  revealed: boolean
  onToggle: () => void
}

interface MapToggleContextType {
  config: MapToggleConfig | null
  setConfig: (config: MapToggleConfig | null) => void
}

const MapToggleContext = createContext<MapToggleContextType | undefined>(
  undefined
)

export function MapToggleProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<MapToggleConfig | null>(null)

  return (
    <MapToggleContext.Provider value={{ config, setConfig }}>
      {children}
    </MapToggleContext.Provider>
  )
}

// Returns null rather than throwing when read outside a provider. The public
// guide-read header renders under ssr:'data-only', where the provider must
// always be present (it's at the root) — but a non-throwing read keeps the
// header resilient if it's ever mounted in a context-free tree.
export function useMapToggle(): MapToggleContextType | null {
  return useContext(MapToggleContext) ?? null
}
